import { v4 as uuidv4 } from "uuid";

import {
  QueryMetrics,
  UtilMetrics,
} from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/EngineMonitoring/types";
import {
  DEFAULT_EXECUTION_CONTEXT,
  DEFAULT_LAYOUT,
  INITIAL_STATE,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/DocumentsContext";
import { expandActiveTabGroup } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/normalizeState";
import {
  DocumentEngineMonitoring,
  DocumentLayout,
  DocumentOutputTab,
  DocumentsState,
  EngineMonitoringInterval,
  EngineMonitoringTimeRange,
  Execution,
  ExecutionContext,
  ExecutionType,
  QueryStatement,
  QueryStatementStatus,
  ScriptType,
  Statistics,
  TabGroup,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

const normalizeContext = (rawContext: any): ExecutionContext => {
  const engineNameFromRawV1 =
    rawContext?.engineName && typeof rawContext.engineName === "string"
      ? rawContext.engineName
      : "";
  const engineNameFromRawV2 =
    rawContext?.engine?.name && typeof rawContext.engine.name === "string"
      ? rawContext.engine.name
      : "";
  const dbNameFromRawV1 =
    rawContext?.databaseName && typeof rawContext.databaseName === "string"
      ? rawContext.databaseName
      : "";
  const dbNameFromRawV2 =
    rawContext?.database?.name && typeof rawContext.database.name === "string"
      ? rawContext.database.name
      : "";
  const context: ExecutionContext = {
    database: {
      name:
        dbNameFromRawV2 ||
        dbNameFromRawV1 ||
        DEFAULT_EXECUTION_CONTEXT.database.name,
      isUserSelected: rawContext?.database?.isUserSelected,
    },
    engine: {
      name:
        engineNameFromRawV2 ||
        engineNameFromRawV1 ||
        DEFAULT_EXECUTION_CONTEXT.engine.name,
      isUserSelected: rawContext?.engine?.isUserSelected,
    },
    settings:
      rawContext?.settings && typeof rawContext.settings === "object"
        ? rawContext.settings
        : DEFAULT_EXECUTION_CONTEXT.settings,
    machineGeneratedSettings:
      rawContext?.machineGeneratedSettings &&
      typeof rawContext.machineGeneratedSettings === "object"
        ? rawContext.machineGeneratedSettings
        : DEFAULT_EXECUTION_CONTEXT.machineGeneratedSettings,
  };

  return context;
};

const normalizeLayout = (rawLayout: any): DocumentLayout => {
  if (typeof rawLayout !== "object" || rawLayout === null) {
    return DEFAULT_LAYOUT;
  }

  const layout: DocumentLayout = {
    editorHeightPx:
      rawLayout?.editorHeightPx && typeof rawLayout.editorHeightPx === "number"
        ? rawLayout.editorHeightPx
        : DEFAULT_LAYOUT.editorHeightPx,
    activeOutputTab:
      rawLayout?.activeOutputTab &&
      rawLayout?.activeOutputTab in DocumentOutputTab
        ? rawLayout.activeOutputTab
        : DEFAULT_LAYOUT.activeOutputTab,
    chatWidthPx:
      typeof rawLayout?.chatWidthPx === "number"
        ? rawLayout.chatWidthPx
        : DEFAULT_LAYOUT.chatWidthPx,
  };

  return layout;
};

const returnNumber = (input: any) => {
  return input && typeof input === "number" ? input : 0;
};

const normalizeStatistics = (rawStatistics: any): Statistics => {
  const statistics: Statistics = {
    executionTimeSec: returnNumber(rawStatistics?.executionTimeSec),
    rowsRead: returnNumber(rawStatistics?.rowsRead),
    bytesRead: returnNumber(rawStatistics?.bytesRead),
    scannedBytesStorage: returnNumber(rawStatistics?.scannedBytesStorage),
    scannedBytesCache: returnNumber(rawStatistics?.scannedBytesCache),
  };

  return statistics;
};

const normalizeExecution = (rawExecution: any): Execution | null => {
  if (typeof rawExecution !== "object" || rawExecution === null) {
    return null;
  }

  const activeQueryStatementIndex =
    rawExecution?.activeQueryStatementIndex &&
    typeof rawExecution.activeQueryStatementIndex === "number"
      ? rawExecution.activeQueryStatementIndex
      : 0;

  const userSelectedActiveQueryStatementIndexTimestamp =
    rawExecution?.userSelectedActiveQueryStatementIndexTimestamp &&
    typeof rawExecution.userSelectedActiveQueryStatementIndexTimestamp ===
      "number"
      ? rawExecution.userSelectedActiveQueryStatementIndexTimestamp
      : 0;

  const queryStatements: QueryStatement[] =
    rawExecution?.queryStatements && Array.isArray(rawExecution.queryStatements)
      ? rawExecution.queryStatements.map(
          (rawQueryStatement: any): QueryStatement => {
            const queryStatement: QueryStatement = {
              id:
                rawQueryStatement?.id &&
                typeof rawQueryStatement.id === "string"
                  ? (rawQueryStatement.id as string)
                  : "query_statement." + uuidv4(),
              content:
                rawQueryStatement?.content &&
                typeof rawQueryStatement.content === "string"
                  ? (rawQueryStatement.content as string)
                  : "",
              responseStatusCode:
                rawQueryStatement?.responseStatusCode &&
                typeof rawQueryStatement.responseStatusCode === "number"
                  ? (rawQueryStatement.responseStatusCode as number)
                  : null,
              status:
                rawQueryStatement?.status &&
                rawQueryStatement.status in QueryStatementStatus
                  ? (rawQueryStatement.status as QueryStatementStatus)
                  : QueryStatementStatus.cancelled,
              prevStatus: rawQueryStatement?.prevStatus,
              resultRows: rawQueryStatement?.resultRows,
              error: null,
              statistics: normalizeStatistics(rawQueryStatement?.statistics),
              location: {
                lineNumber:
                  rawQueryStatement?.location?.lineNumber &&
                  typeof rawQueryStatement.location.lineNumber === "number"
                    ? (rawQueryStatement.location.lineNumber as number)
                    : 0,
                start:
                  rawQueryStatement?.location?.start &&
                  typeof rawQueryStatement.location.start === "number"
                    ? (rawQueryStatement.location.start as number)
                    : 0,
                end:
                  rawQueryStatement?.location?.end &&
                  typeof rawQueryStatement.location.end === "number"
                    ? (rawQueryStatement.location.end as number)
                    : 0,
              },
            };

            // Preserve additional fields
            if (rawQueryStatement.serverQueryId) {
              Object.assign(queryStatement, {
                serverQueryId: rawQueryStatement.serverQueryId,
              });
            }
            if (rawQueryStatement.explainType) {
              Object.assign(queryStatement, {
                explainType: rawQueryStatement.explainType,
              });
            }
            if (rawQueryStatement.executionType) {
              Object.assign(queryStatement, {
                executionType: rawQueryStatement.executionType,
              });
            }

            return queryStatement;
          }
        )
      : [];

  const execution: Execution = {
    executionType: ExecutionType.Query,
    activeQueryStatementIndex,
    userSelectedActiveQueryStatementIndexTimestamp,
    queryStatements,
    documentExecutionError: null, // always reset the value
  };

  return execution;
};

const normalizeEngineMonitoringTimeRange = (
  rawTimeRange: any
): EngineMonitoringTimeRange => {
  if (!rawTimeRange) {
    return null;
  }

  if (rawTimeRange?.start && rawTimeRange?.end) {
    try {
      const startDate: Date = new Date(rawTimeRange.start);
      const endDate: Date = new Date(rawTimeRange.end);

      return {
        start: startDate,
        end: endDate,
      };
    } catch (e) {
      return null;
    }
  }

  if (
    typeof rawTimeRange === "string" &&
    Object.values(EngineMonitoringInterval).includes(
      rawTimeRange as EngineMonitoringInterval
    )
  ) {
    return rawTimeRange as EngineMonitoringInterval;
  }

  return null;
};

const normalizeEngineMonitoring = (
  rawEngineMonitoring: any
): DocumentEngineMonitoring => {
  if (
    rawEngineMonitoring &&
    typeof rawEngineMonitoring === "object" &&
    rawEngineMonitoring?.timeRange !== undefined &&
    rawEngineMonitoring?.utilChart?.highlightedKeys &&
    rawEngineMonitoring?.utilChart?.highlightedKeys.every((k: any) =>
      Object.values(UtilMetrics).includes(k)
    ) &&
    rawEngineMonitoring?.queriesChart?.highlightedKeys &&
    rawEngineMonitoring?.queriesChart?.highlightedKeys.every((k: any) =>
      Object.values(QueryMetrics).includes(k)
    )
  ) {
    return {
      timeRange: normalizeEngineMonitoringTimeRange(
        rawEngineMonitoring.timeRange
      ),
      utilChart: {
        highlightedKeys: rawEngineMonitoring.utilChart
          .highlightedKeys as UtilMetrics[],
      },
      queriesChart: {
        highlightedKeys: rawEngineMonitoring.queriesChart
          .highlightedKeys as QueryMetrics[],
      },
    };
  }

  return {
    timeRange: null,
    utilChart: {
      highlightedKeys: [],
    },
    queriesChart: {
      highlightedKeys: [],
    },
  };
};

const normalizeDocument = (rawDocument: any): WorkspaceDocument => {
  const document: WorkspaceDocument = {
    context: normalizeContext(rawDocument?.context),
    id:
      rawDocument?.id && typeof rawDocument?.id === "string"
        ? (rawDocument.id as string)
        : "",
    script: {
      type: rawDocument?.script?.type || "",
      id: rawDocument?.script?.id || "",
    },
    aiState: null,
    layout: normalizeLayout(rawDocument?.layout),
    execution: normalizeExecution(rawDocument?.execution),
    engineMonitoring: normalizeEngineMonitoring(rawDocument?.engineMonitoring),
    createdAt:
      rawDocument?.createdAt && typeof rawDocument.createdAt === "number"
        ? (rawDocument.createdAt as number)
        : Date.now(),
    groupId:
      rawDocument?.groupId && typeof rawDocument.groupId === "string"
        ? (rawDocument.groupId as string)
        : null,
  };

  return document;
};

const normalizeTabGroups = (rawTabGroups: any): TabGroup[] => {
  if (!Array.isArray(rawTabGroups)) {
    return [];
  }

  return rawTabGroups
    .filter(
      (rawTabGroup: any) =>
        typeof rawTabGroup?.id === "string" &&
        typeof rawTabGroup?.name === "string" &&
        typeof rawTabGroup?.color === "string"
    )
    .map((rawTabGroup: any) => ({
      id: rawTabGroup.id as string,
      name: rawTabGroup.name as string,
      color: rawTabGroup.color as string,
      collapsed: rawTabGroup.collapsed === true,
    }));
};

/*
  The bar draws a group as one continuous span which only works while its tabs sit
  contiguously in the array. Stored state is written that way, but it is stored in
  the browser and can be returned edited, so the runs are rebuilt here.
  Each tab keeps the position of the first tab in its parent group, and a tab pointing
  at a group that did not survive the filter above is ungrouped.
 */
const normalizeTabGroupRuns = (
  documents: WorkspaceDocument[],
  tabGroups: TabGroup[]
): WorkspaceDocument[] => {
  const runs = new Map<string | null, WorkspaceDocument[]>();

  documents.forEach(document => {
    const groupId = tabGroups.some(tabGroup => tabGroup.id === document.groupId)
      ? document.groupId
      : null;

    const key = groupId ?? document.id;

    if (!runs.has(key)) {
      runs.set(key, []);
    }

    runs.get(key)!.push({ ...document, groupId });
  });

  return [...runs.values()].flat();
};

const isDocumentValid = (document: any): boolean => {
  if (
    document?.id &&
    typeof document.id === "string" &&
    document?.script?.id &&
    typeof document.script.id === "string" &&
    document?.script?.type &&
    (document.script.type === ScriptType.local ||
      document.script.type === ScriptType.remote)
  ) {
    return true;
  }

  return false;
};

const normalizeDocumentsRawState = (rawState: any): DocumentsState => {
  if (typeof rawState !== "object" || rawState === null) {
    return INITIAL_STATE;
  }

  const tabGroups = normalizeTabGroups(rawState?.tabGroups);

  const documents =
    rawState?.documents && Array.isArray(rawState?.documents)
      ? rawState.documents.filter(isDocumentValid).map(normalizeDocument)
      : [];

  const orderedDocuments = normalizeTabGroupRuns(documents, tabGroups);

  /*
  Restored state is used as the initial state of the reducer, so it is never visible to
  normalizeState and a pointer to a tab that did not survive the filter above would leave
  the pane blank until the next dispatch.
  Falls back to the first tab like normalizeState does.
   */
  const activeDocumentId = orderedDocuments.some(
    document => document.id === rawState?.activeDocumentId
  )
    ? (rawState.activeDocumentId as string)
    : (orderedDocuments[0]?.id ?? null);

  return expandActiveTabGroup({
    documents: orderedDocuments,
    activeDocumentId,
    // a group whose tabs are all gone is not restored
    tabGroups: tabGroups.filter(tabGroup =>
      documents.some(
        (document: WorkspaceDocument) => document.groupId === tabGroup.id
      )
    ),
  });
};

export default normalizeDocumentsRawState;
